// Crash-recovery journal: the latest not-yet-persisted copy of each note being edited.
// Written synchronously on every change (small, text-only), cleared once IndexedDB confirms the save.
import { NoteSchema, type Note } from './schemas';

const KEY = 'memonest.journal.v1';

type Journal = Record<string, { savedAt: number; note: Note }>;

function read(): Journal {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Journal) : {};
  } catch {
    return {};
  }
}

function write(j: Journal): boolean {
  try {
    if (Object.keys(j).length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(j));
    return true;
  } catch {
    return false; // quota or disabled storage — IndexedDB save is still attempted
  }
}

export function journalPut(note: Note): void {
  const j = read();
  j[note.id] = { savedAt: Date.now(), note };
  write(j);
}

export function journalClear(noteId: string, rev?: number): void {
  const j = read();
  const entry = j[noteId];
  if (!entry) return;
  // Only clear if nothing newer has been journaled since the save started.
  if (rev === undefined || entry.note.rev <= rev) {
    delete j[noteId];
    write(j);
  }
}

/** Entries that are newer than what IndexedDB holds — recovered on startup. */
export function journalRecover(stored: Map<string, Note>): Note[] {
  const j = read();
  const out: Note[] = [];
  for (const [id, entry] of Object.entries(j)) {
    const parsed = NoteSchema.safeParse(entry?.note);
    if (!parsed.success) {
      delete j[id];
      continue;
    }
    const s = stored.get(id);
    if (!s || parsed.data.updatedAt > s.updatedAt) out.push(parsed.data);
    else delete j[id];
  }
  write(j);
  return out;
}
